import { useEffect, useMemo, useState } from 'react'
import { Button } from '@unified-lk/ui'
import type { StaffUsersData } from '../../types/plans'
import type { StaffUsersCatalogResponse, StaffUsersWriteUser } from '@unified-lk/api-contracts'
import {
  workspaceActionsClassName,
  workspaceBadgeClassName,
  workspaceCardClassName,
  workspaceEmptyClassName,
  workspaceGridClassName,
  workspaceHeaderClassName,
  workspaceKickerClassName,
  workspaceMetricClassName,
  workspaceMutedTextClassName,
  workspacePanelClassName,
  workspaceSectionHeaderClassName,
  workspaceSummaryGridClassName,
  workspaceTagClassName,
} from '../workspaceStyles'
import { WorkspacePagination, WorkspaceSearchPanel, WorkspaceSelectFilter } from '../shared/WorkspaceListControls'

type UserRow = StaffUsersData['users'][number]

type UsersCatalog = {
  permissions: StaffUsersCatalogResponse['permissions']
  roles: StaffUsersCatalogResponse['roles']
}

interface FormState {
  id: number | null
  phone: string
  full_name: string
  display_name: string
  email: string
  role_key: string
  branch_ids: number[]
  permissions: string[]
  linked_doctor_id: string
  is_active: boolean
}

const KNOWN_BRANCHES: { id: number; label: string }[] = [
  { id: 1, label: 'Пресненская' },
  { id: 2, label: 'Автозаводская' },
  { id: 3, label: 'Сити' },
  { id: 4, label: 'Таганская' },
]

const USERS_PAGE_SIZE = 10

function emptyForm(): FormState {
  return {
    id: null,
    phone: '',
    full_name: '',
    display_name: '',
    email: '',
    role_key: 'doctor',
    branch_ids: [],
    permissions: [],
    linked_doctor_id: '',
    is_active: true,
  }
}

function fromUser(user: UserRow): FormState {
  return {
    id: user.id,
    phone: user.phone ?? '',
    full_name: user.full_name ?? user.display_name ?? '',
    display_name: user.display_name ?? '',
    email: user.email ?? '',
    role_key: user.role_key ?? 'doctor',
    branch_ids: [...(user.branches ?? [])],
    permissions: [...(user.permissions ?? [])],
    linked_doctor_id: user.linked_doctor_id != null ? String(user.linked_doctor_id) : '',
    is_active: user.is_active ?? true,
  }
}

export function StaffUsersSection({
  data,
  loading,
  error,
  onRetry,
  canIssueSecretLinks = false,
  onIssueStaffSecretLink,
  canManageUsers = false,
  catalog,
  onLoadCatalog,
  onCreateUser,
  onUpdateUser,
  onSetUserActive,
}: {
  data: StaffUsersData | null
  loading: boolean
  error: string | null
  onRetry: () => void
  canIssueSecretLinks?: boolean
  onIssueStaffSecretLink?: (userId: number) => Promise<string>
  canManageUsers?: boolean
  catalog?: UsersCatalog | null
  onLoadCatalog?: () => Promise<void> | void
  onCreateUser?: (payload: Omit<StaffUsersWriteUser, 'id'>) => Promise<void>
  onUpdateUser?: (userId: number, patch: Partial<Omit<StaffUsersWriteUser, 'id'>>) => Promise<void>
  onSetUserActive?: (userId: number, isActive: boolean) => Promise<void>
}) {
  const [issuingUserId, setIssuingUserId] = useState<number | null>(null)
  const [copiedUserId, setCopiedUserId] = useState<number | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create')
  const [form, setForm] = useState<FormState>(emptyForm)
  const [submitting, setSubmitting] = useState(false)
  const [editorError, setEditorError] = useState<string | null>(null)
  const [activatingUserId, setActivatingUserId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('active')
  const [page, setPage] = useState(1)

  useEffect(() => {
    if (canManageUsers && !catalog && onLoadCatalog) {
      void onLoadCatalog()
    }
  }, [canManageUsers, catalog, onLoadCatalog])

  const branchOptions = useMemo(() => {
    const branches = new Map<number, string>()
    for (const branch of KNOWN_BRANCHES) branches.set(branch.id, branch.label)
    if (data) {
      for (const user of data.users) {
        for (let i = 0; i < user.branches.length; i += 1) {
          const id = user.branches[i]
          const label = user.branch_labels[i] ?? `Филиал #${id}`
          if (!branches.has(id)) branches.set(id, label)
        }
      }
    }
    return Array.from(branches.entries()).map(([id, label]) => ({ id, label }))
  }, [data])

  const roleOptions = useMemo(() => {
    const roles = new Map<string, string>()
    for (const role of catalog?.roles ?? []) roles.set(role.key, role.label)
    for (const user of data?.users ?? []) roles.set(user.role_key, user.role_label || user.role_key)

    return [
      { value: 'all', label: 'Все роли' },
      ...Array.from(roles.entries()).map(([value, label]) => ({ value, label })),
    ]
  }, [catalog, data])

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    return (data?.users ?? []).filter((user) => {
      if (roleFilter !== 'all' && user.role_key !== roleFilter) {
        return false
      }
      if (statusFilter === 'active' && user.is_active === false) {
        return false
      }
      if (statusFilter === 'inactive' && user.is_active !== false) {
        return false
      }
      if (!query) {
        return true
      }

      const haystack = [
        user.display_name,
        user.full_name ?? '',
        user.phone,
        user.email ?? '',
        user.role_label,
        user.role_key,
        user.linked_doctor_id != null ? String(user.linked_doctor_id) : '',
        ...user.branch_labels,
        ...user.permissions,
      ].join(' ').toLowerCase()

      return haystack.includes(query)
    })
  }, [data, roleFilter, searchQuery, statusFilter])

  const totalPages = Math.max(Math.ceil(filteredUsers.length / USERS_PAGE_SIZE), 1)
  const pagedUsers = useMemo(() => {
    const safePage = Math.min(Math.max(page, 1), totalPages)
    const start = (safePage - 1) * USERS_PAGE_SIZE

    return filteredUsers.slice(start, start + USERS_PAGE_SIZE)
  }, [filteredUsers, page, totalPages])

  useEffect(() => {
    setPage(1)
  }, [roleFilter, searchQuery, statusFilter])

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages)
    }
  }, [page, totalPages])

  function openCreate() {
    setForm(emptyForm())
    setEditorMode('create')
    setEditorOpen(true)
    setEditorError(null)
  }

  function openEdit(user: UserRow) {
    setForm(fromUser(user))
    setEditorMode('edit')
    setEditorOpen(true)
    setEditorError(null)
  }

  function applyRolePreset(roleKey: string) {
    const preset = catalog?.roles.find((entry) => entry.key === roleKey)
    setForm((current) => ({
      ...current,
      role_key: roleKey,
      permissions: preset ? [...preset.permissions] : current.permissions,
    }))
  }

  function togglePermission(permission: string) {
    setForm((current) => ({
      ...current,
      permissions: current.permissions.includes(permission)
        ? current.permissions.filter((entry) => entry !== permission)
        : [...current.permissions, permission],
    }))
  }

  function toggleBranch(branchId: number) {
    setForm((current) => ({
      ...current,
      branch_ids: current.branch_ids.includes(branchId)
        ? current.branch_ids.filter((entry) => entry !== branchId)
        : [...current.branch_ids, branchId],
    }))
  }

  async function submitForm() {
    if (!onCreateUser || !onUpdateUser) return
    const phone = form.phone.trim()
    const fullName = form.full_name.trim()
    if (!phone) {
      setEditorError('Укажите телефон сотрудника')
      return
    }
    if (!fullName) {
      setEditorError('Укажите ФИО сотрудника')
      return
    }
    setSubmitting(true)
    setEditorError(null)
    try {
      const payload = {
        phone,
        full_name: fullName,
        display_name: form.display_name.trim() || fullName,
        email: form.email.trim() || null,
        role_key: form.role_key,
        branch_ids: form.branch_ids,
        permissions: form.permissions,
        linked_doctor_id: form.linked_doctor_id.trim() ? Number(form.linked_doctor_id.trim()) : null,
        is_active: form.is_active,
      }
      if (editorMode === 'create') {
        await onCreateUser(payload)
      } else if (form.id != null) {
        await onUpdateUser(form.id, payload)
      }
      setEditorOpen(false)
    } catch (reason) {
      setEditorError(reason instanceof Error ? reason.message : 'Не удалось сохранить пользователя')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleToggleActive(user: UserRow) {
    if (!onSetUserActive) return
    setActivatingUserId(user.id)
    try {
      await onSetUserActive(user.id, !user.is_active)
    } finally {
      setActivatingUserId(null)
    }
  }

  async function handleIssueSecretLink(userId: number) {
    if (!onIssueStaffSecretLink) {
      return
    }

    setIssuingUserId(userId)
    try {
      const url = await onIssueStaffSecretLink(userId)
      try {
        await navigator.clipboard.writeText(url)
        setCopiedUserId(userId)
        window.setTimeout(() => setCopiedUserId((current) => (current === userId ? null : current)), 2000)
      } catch {
        // ignore clipboard errors
      }
      window.open(url, '_blank', 'noopener,noreferrer')
    } finally {
      setIssuingUserId(null)
    }
  }

  if (loading) {
    return <div className={workspaceEmptyClassName}><p>Загружаем пользователей и права...</p></div>
  }

  if (!data) {
    return (
      <div className={workspaceEmptyClassName}>
        <p>{error ?? 'Раздел пользователей пока недоступен.'}</p>
        <div className={workspaceActionsClassName}>
          <Button variant="secondary" type="button" onClick={onRetry}>Повторить</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={workspaceGridClassName}>
      <section className={workspacePanelClassName}>
        <div className={workspaceHeaderClassName}>
          <div className="grid gap-2">
            <p className={workspaceKickerClassName}>Users / Roles</p>
            <h2 className="text-2xl font-semibold text-slate-900">Пользователи и права</h2>
            <p className={workspaceMutedTextClassName}>
              Управление сотрудниками: создание, изменение прав, активация и деактивация без обращения к админу.
            </p>
          </div>
          <div className={workspaceActionsClassName}>
            {canManageUsers ? (
              <Button variant="primary" type="button" onClick={openCreate}>+ Новый сотрудник</Button>
            ) : null}
            <Button variant="secondary" type="button" onClick={onRetry}>Обновить</Button>
          </div>
        </div>

        <div className={workspaceSummaryGridClassName}>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Сотрудников</span><strong className="text-slate-900">{data.summary.staff_total}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Ролей</span><strong className="text-slate-900">{data.summary.roles_total}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">Филиалов</span><strong className="text-slate-900">{data.summary.branches_total}</strong></div>
          <div className={workspaceMetricClassName}><span className="text-[13px] text-slate-500">В выборке</span><strong className="text-slate-900">{filteredUsers.length}</strong></div>
        </div>

        <WorkspaceSearchPanel
          label="Поиск по сотрудникам"
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="ФИО, телефон, роль, филиал, право или ID врача в МИС"
        >
          <WorkspaceSelectFilter
            label="Роль"
            value={roleFilter}
            onChange={setRoleFilter}
            options={roleOptions}
          />
          <WorkspaceSelectFilter
            label="Статус"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'active', label: 'Активные' },
              { value: 'all', label: 'Все' },
              { value: 'inactive', label: 'Неактивные' },
            ]}
          />
        </WorkspaceSearchPanel>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        {pagedUsers.map((user) => (
          <article
            key={user.id}
            className={`${workspaceCardClassName} ${user.is_active === false ? 'opacity-60' : ''}`}
          >
            <div className={workspaceSectionHeaderClassName}>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{user.display_name}</h3>
                <p className={workspaceMutedTextClassName}>{user.email ?? user.phone}</p>
              </div>
              <span className={workspaceBadgeClassName(user.is_current_user)}>{user.role_label}</span>
            </div>
            {user.full_name && user.full_name !== user.display_name ? (
              <p className="text-sm text-slate-600">{user.full_name}</p>
            ) : null}
            <div className={workspaceActionsClassName}>
              {user.branch_labels.map((branch) => (
                <span key={branch} className={workspaceTagClassName()}>{branch}</span>
              ))}
            </div>
            <div className={workspaceActionsClassName}>
              {user.permissions.map((permission) => (
                <span key={permission} className={workspaceTagClassName(user.is_current_user)}>{permission}</span>
              ))}
            </div>
            <div className={workspaceActionsClassName}>
              {canManageUsers ? (
                <>
                  <Button variant="secondary" type="button" onClick={() => openEdit(user)}>Редактировать</Button>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => void handleToggleActive(user)}
                    disabled={activatingUserId === user.id}
                  >
                    {activatingUserId === user.id
                      ? 'Сохраняем...'
                      : user.is_active === false
                        ? 'Активировать'
                        : 'Деактивировать'}
                  </Button>
                </>
              ) : null}
              {canIssueSecretLinks ? (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => void handleIssueSecretLink(user.id)}
                  disabled={issuingUserId === user.id}
                >
                  {issuingUserId === user.id
                    ? 'Формируем ссылку...'
                    : copiedUserId === user.id
                      ? 'Ссылка скопирована'
                      : 'Secret вход'}
                </Button>
              ) : null}
            </div>
          </article>
        ))}
        {pagedUsers.length === 0 ? (
          <div className={`${workspaceEmptyClassName} xl:col-span-2`}>
            По текущим фильтрам сотрудников не найдено.
          </div>
        ) : null}
      </section>

      <WorkspacePagination
        page={page}
        pages={totalPages}
        total={filteredUsers.length}
        pageSize={USERS_PAGE_SIZE}
        itemLabel="сотрудников"
        onPrevPage={() => setPage((current) => Math.max(current - 1, 1))}
        onNextPage={() => setPage((current) => Math.min(current + 1, totalPages))}
      />

      <section className={workspacePanelClassName}>
        <div className={workspaceSectionHeaderClassName}>
          <h3 className="text-xl font-semibold text-slate-900">Матрица доступа</h3>
          <span className="text-sm text-slate-500">{data.permission_matrix.length}</span>
        </div>
        <ul className="grid gap-3 p-0">
          {data.permission_matrix.map((entry) => (
            <li key={entry.permission} className="list-none rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4 text-sm leading-6 text-slate-700">
              <strong className="block text-slate-900">{entry.label}</strong>
              <span>{entry.holders.length ? entry.holders.join(', ') : 'Нет назначений'}</span>
            </li>
          ))}
        </ul>
      </section>

      {editorOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-6">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-[24px] bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className={workspaceKickerClassName}>{editorMode === 'create' ? 'Новый сотрудник' : 'Редактирование сотрудника'}</p>
                <h3 className="text-2xl font-semibold text-slate-900">
                  {editorMode === 'create' ? 'Создать пользователя' : (form.display_name || form.full_name || 'Без имени')}
                </h3>
              </div>
              <Button variant="secondary" type="button" onClick={() => setEditorOpen(false)}>Закрыть</Button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="grid gap-1 text-sm text-slate-700">
                <span>Телефон *</span>
                <input
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={form.phone}
                  onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                  placeholder="+7 ..."
                />
              </label>
              <label className="grid gap-1 text-sm text-slate-700">
                <span>ФИО *</span>
                <input
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={form.full_name}
                  onChange={(event) => setForm((current) => ({ ...current, full_name: event.target.value }))}
                />
              </label>
              <label className="grid gap-1 text-sm text-slate-700">
                <span>Отображаемое имя</span>
                <input
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={form.display_name}
                  onChange={(event) => setForm((current) => ({ ...current, display_name: event.target.value }))}
                  placeholder="По умолчанию = ФИО"
                />
              </label>
              <label className="grid gap-1 text-sm text-slate-700">
                <span>Email</span>
                <input
                  type="email"
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={form.email}
                  onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                />
              </label>
              <label className="grid gap-1 text-sm text-slate-700">
                <span>Роль</span>
                <select
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={form.role_key}
                  onChange={(event) => applyRolePreset(event.target.value)}
                >
                  {(catalog?.roles ?? []).map((role) => (
                    <option key={role.key} value={role.key}>{role.label}</option>
                  ))}
                  {!catalog ? <option value={form.role_key}>{form.role_key}</option> : null}
                </select>
              </label>
              <label className="grid gap-1 text-sm text-slate-700">
                <span>ID врача в МИС (опц.)</span>
                <input
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  inputMode="numeric"
                  value={form.linked_doctor_id}
                  onChange={(event) => setForm((current) => ({ ...current, linked_doctor_id: event.target.value }))}
                  placeholder="напр. 19"
                />
              </label>
            </div>

            <div className="mt-6 grid gap-2">
              <span className="text-sm font-medium text-slate-700">Филиалы</span>
              <div className="flex flex-wrap gap-2">
                {branchOptions.map((branch) => {
                  const active = form.branch_ids.includes(branch.id)
                  return (
                    <button
                      key={branch.id}
                      type="button"
                      onClick={() => toggleBranch(branch.id)}
                      className={`rounded-full border px-3 py-1 text-xs ${active ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-white text-slate-600'}`}
                    >
                      {branch.label}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="mt-6 grid gap-2">
              <span className="text-sm font-medium text-slate-700">Права доступа</span>
              <div className="grid gap-2 sm:grid-cols-2">
                {(catalog?.permissions ?? []).map((permission) => {
                  const active = form.permissions.includes(permission.key)
                  return (
                    <label key={permission.key} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${active ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => togglePermission(permission.key)}
                      />
                      <span className="font-medium text-slate-800">{permission.label}</span>
                      <span className="ml-auto text-xs text-slate-500">{permission.key}</span>
                    </label>
                  )
                })}
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <input
                id="lk-user-active"
                type="checkbox"
                checked={form.is_active}
                onChange={(event) => setForm((current) => ({ ...current, is_active: event.target.checked }))}
              />
              <label htmlFor="lk-user-active" className="text-sm text-slate-700">Активен (может входить в ЛК)</label>
            </div>

            {editorError ? (
              <p className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{editorError}</p>
            ) : null}

            <div className="mt-6 flex items-center justify-end gap-2">
              <Button variant="secondary" type="button" onClick={() => setEditorOpen(false)} disabled={submitting}>Отмена</Button>
              <Button variant="primary" type="button" onClick={() => void submitForm()} disabled={submitting}>
                {submitting ? 'Сохраняем...' : editorMode === 'create' ? 'Создать' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
